import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { UseTimeZone } from '../../../../common/hooks/useTimeZone'
import { Element } from '../../../../common/html/element'
import { Loading } from '../../../../common/loading'
import { Notification } from '../../../../common/notification'
import { Table } from '../../../../common/table'
import { TextEllipsisStaticLine } from '../../../../common/textEllipsis/TextEllipsisStaticLine'
import { FORMAT, formatDateTime } from '../../../../helpers/datetime'
import { EMPTY_VALUE } from '../../../../utils/common'
import '../../lang/i18n'
import {
  selectLoading,
  selectTopComparablePlayers,
  selectTopComparablePlayersById,
} from '../../store/slice'
import { Company } from './Company'
import { COLUMN_IDS, getLocalizedValue, normalizePublicDate } from './helper'
import classes from './index.module.scss'

const COLUMN_WIDTHS = {
  [COLUMN_IDS.RANK]: '4%',
  [COLUMN_IDS.COMPANY]: '24%',
  [COLUMN_IDS.SIMILARITY_SCORE]: '9%',
  [COLUMN_IDS.INDUSTRY]: '28%',
  [COLUMN_IDS.COMPANY_SIZE]: '13%',
  [COLUMN_IDS.INVESTMENT_TYPE]: '11%',
  [COLUMN_IDS.INTERNATIONAL_TRADE]: '11%',
}

export const TopComparablePlayers = ({ isDynamicReport }) => {
  const locale = useSelector((state) => state.i18n.locale)
  const timezone = UseTimeZone()
  const { data } = useSelector(selectTopComparablePlayers())
  const isLoading = useSelector(selectLoading('topComparablePlayers'))

  const schema = Object.keys(COLUMN_IDS).map((key) => {
    const colId = COLUMN_IDS[key]
    return {
      colId,
      title: `companySummary.topComparablePlayers.${key}`,
      isI18n: true,
      parentThStyle: {
        width: COLUMN_WIDTHS[colId],
      },
      thStyle: {
        justifyContent: [COLUMN_IDS.SIMILARITY_SCORE].includes(colId)
          ? 'flex-end'
          : '',
        whiteSpace: isDynamicReport ? 'normal' : undefined,
      },
      tdStyle: {
        width: COLUMN_WIDTHS[colId],
        textAlign: [COLUMN_IDS.SIMILARITY_SCORE].includes(colId)
          ? 'right'
          : 'left',
        overflowWrap: isDynamicReport ? 'anywhere' : undefined,
        whiteSpace: isDynamicReport
          ? [COLUMN_IDS.RANK, COLUMN_IDS.SIMILARITY_SCORE].includes(colId)
            ? 'nowrap'
            : 'normal'
          : colId === COLUMN_IDS.COMPANY
          ? ''
          : 'nowrap',
      },
      render: (value, { rowId }) => {
        const row = data.find((item) => item.rank === rowId)
        switch (colId) {
          case COLUMN_IDS.COMPANY:
            return <Company rowId={rowId} isDynamicReport={isDynamicReport} />
          case COLUMN_IDS.SIMILARITY_SCORE:
            return Number.isFinite(value)
              ? Number(value.toFixed(2)).toLocaleString(locale, {
                  maximumFractionDigits: 2,
                })
              : EMPTY_VALUE
          case COLUMN_IDS.INDUSTRY:
            const industryName =
              getLocalizedValue(row, 'mainVSICName', locale) || EMPTY_VALUE
            return isDynamicReport ? (
              industryName
            ) : (
              <TextEllipsisStaticLine
                text={industryName}
                isI18n={false}
                lineClamp={1}
              />
            )
          case COLUMN_IDS.COMPANY_SIZE:
            return (
              getLocalizedValue(row, 'companySizeName', locale) || EMPTY_VALUE
            )
          case COLUMN_IDS.INVESTMENT_TYPE:
            return (
              getLocalizedValue(row, 'investmentTypeName', locale) ||
              EMPTY_VALUE
            )
          case COLUMN_IDS.INTERNATIONAL_TRADE:
            return (
              getLocalizedValue(row, 'internationalTradeName', locale) ||
              EMPTY_VALUE
            )
          default:
            return (
              <TextEllipsisStaticLine
                text={value}
                isI18n={false}
                lineClamp={1}
              />
            )
        }
      },
    }
  })

  return (
    <Element className={classes.container}>
      {isLoading ? (
        <Loading />
      ) : !data.length ? (
        <Notification />
      ) : (
        <>
          <Element className={classes.tableContainer}>
            <Table
              ids={data.map((item) => item.rank)}
              getDataFromRedux={selectTopComparablePlayersById}
              schema={schema}
              autoHeightMax={isDynamicReport ? 332 : undefined}
              isDisplayScroll={!isDynamicReport}
              isNoStickyHead={isDynamicReport}
              isDynamicReport={isDynamicReport}
            />
          </Element>
          <Element
            className={classes.lastUpdatedDate}
            style={{ borderColor: '#B5CEF8', color: '#3A414B' }}
          >
            <Translate value="companySummary.topComparablePlayers.LAST_UPDATED_DATE" />
            :&nbsp;
            {formatDateTime(
              normalizePublicDate(data[0]?.publicDateId),
              FORMAT.DATE,
              locale,
              timezone,
            )}
          </Element>
        </>
      )}
    </Element>
  )
}

TopComparablePlayers.propTypes = {
  isDynamicReport: PropTypes.bool,
}

TopComparablePlayers.defaultProps = {
  isDynamicReport: false,
}
